import { Resource } from 'src_common/common/mobx-utils/Resource';
import { makeAutoObservable } from 'mobx';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { CampaignModel } from './CampaignModel';
import { ApiGetCampaignsResponse200Type } from 'src/api_openapi/v2/bonus-engine/apiGetCampaigns';
import { ApiCreateCampaignResponseType } from 'src/api_openapi/v2/bonus-engine/apiCreateCampaign';
import {
    BonusCampaignFormType,
    CampaignStatusType,
    CampaignUpdateFormType,
    PromoCodeUpdateFormType,
} from 'src/api_openapi/v2/bonus-engine/schemas';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { Common } from 'src/AppState/Common';
import { ApiUpdateCampaignResponseType } from 'src/api_openapi/v2/bonus-engine/apiUpdateCampaign';
import { ApiUpdateCampaignStatusResponseType } from 'src/api_openapi/v2/bonus-engine/apiUpdateCampaignStatus';
import { ApiActivateCampaignResponseType } from 'src/api_openapi/v2/bonus-engine/apiActivateCampaign';
import { ApiExtendCampaignEndDateResponseType } from 'src/api_openapi/v2/bonus-engine/apiExtendCampaignEndDate';
import { ApiUpdatePromoCodeResponseType } from 'src/api_openapi/v2/bonus-engine/apiUpdatePromoCode';
import { ToasterState } from 'src/AppState/ToasterState';

export class CampaignStore {
    public campaignsResource: AutoMap<string, Resource<ApiGetCampaignsResponse200Type>>;
    public isPromoCodeUpdating: boolean = false;

    public static get = AutoWeakMap.create((common: Common) => new CampaignStore(common));

    private constructor(private common: Common) {
        makeAutoObservable(this);
        this.campaignsResource = new AutoMap((_key: string) => {
            return new Resource(async (): Promise<ApiGetCampaignsResponse200Type> => {
                const response = await this.common.trpcClient.client.bonusCampaignsRouter.getBonusCampaings.mutate();

                if (response.status === 200) {
                    return response.body;
                }
                return [];
            });
        });
    }

    public get allCampaigns(): Array<CampaignModel> {
        const resource = this.campaignsResource.get('');
        const result = resource.get();
        if (result.type === 'ready') {
            return result.value.map((data) => new CampaignModel(data));
        } else {
            return [];
        }
    }

    public getCampaign = (campaignId: string): CampaignModel | null => {
        return this.allCampaigns.find((campaign) => campaign.id === campaignId) ?? null; // TODO: consider change it to SingleCampaign.store.ts
    };

    public createCampaign = async (campaign: BonusCampaignFormType): Promise<ApiCreateCampaignResponseType> => {
        const response = await this.common.trpcClient.client.bonusCampaignsRouter.createBonusCampaign.mutate(campaign);
        await this.refreshCampaigns();
        return response;
    };

    public deleteCampaign = async (campaignId: string): Promise<void> => {
        await this.common.trpcClient.client.bonusCampaignsRouter.deleteBonusCampaign.mutate({
            campaignId,
        });
        await this.refreshCampaigns();
    };

    public updateCampaign = async (campaign: {
        campaignId: string;
        changeStartAt: boolean;
        requestBody: CampaignUpdateFormType;
    }): Promise<ApiUpdateCampaignResponseType> => {
        const response = await this.common.trpcClient.client.bonusCampaignsRouter.updateBonusCampaign.mutate({
            campaignId: campaign.campaignId,
            requestBody: campaign.requestBody,
            changeStartAt: campaign.changeStartAt,
        });
        await this.refreshCampaigns();
        return response;
    };

    public updateCampaignEndDate = async (campaign: {
        campaignId: string;
        expiryAt: string;
    }): Promise<ApiExtendCampaignEndDateResponseType> => {
        const response = await this.common.trpcClient.client.bonusCampaignsRouter.updateBonusCampaignDate.mutate({
            campaignId: campaign.campaignId,
            expiryAt: campaign.expiryAt,
        });
        await this.refreshCampaigns();
        return response;
    };

    public updateCampaignStatus = async (campaign: {
        campaignId: string;
        status: CampaignStatusType;
    }): Promise<ApiUpdateCampaignStatusResponseType> => {
        const response = await this.common.trpcClient.client.bonusCampaignsRouter.updateBonusCampaignStatus.mutate({
            campaignId: campaign.campaignId,
            requestBody: {
                status: campaign.status,
            },
        });
        await this.refreshCampaigns();
        return response;
    };

    public activateCampaign = async (campaignId: string): Promise<ApiActivateCampaignResponseType> => {
        const response = await this.common.trpcClient.client.bonusCampaignsRouter.setCampaignAsActive.mutate({
            campaignId,
        });
        await this.refreshCampaigns();
        return response;
    };

    public updatePromoCode = async (
        campaignId: string,
        promoCode: string,
        requestBody: PromoCodeUpdateFormType
    ): Promise<ApiUpdatePromoCodeResponseType> => {
        this.isPromoCodeUpdating = true;
        const response = await this.common.trpcClient.client.bonusCampaignsRouter.updatePromoCode.mutate({
            campaignId,
            promoCode,
            requestBody,
        });
        const action = requestBody.isPaused === true ? 'paused' : 'activated';
        if (response.status === 200) {
            ToasterState.get(this.common).setToast({
                title: 'Success!',
                variant: 'success',
                message: `Promo code ${promoCode} was successfully ${action}.`,
            });
        } else {
            ToasterState.get(this.common).setToast({
                title: 'Error!',
                variant: 'error',
                message: response.body.message,
            });
        }
        await this.refreshCampaigns();
        this.isPromoCodeUpdating = false;

        // if response body is unknown, TRPC mistakenly treats it as optional (undefined) - expected: body: unknown, result body?: unknown
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        return response as ApiUpdatePromoCodeResponseType;
    };

    public refreshCampaigns = async (): Promise<void> => {
        await this.campaignsResource.get('').refresh();
    };

    public get isLoading(): boolean {
        const resource = this.campaignsResource.get('');
        const result = resource.get();
        return result.type === 'loading';
    }

    public get hasZeroCampaigns(): boolean {
        const resource = this.campaignsResource.get('');
        const result = resource.get();
        return result.type === 'ready' && result.value.length === 0;
    }
}
